// @ts-nocheck
/**
 * Educational Success Stories Page
 * Built with IBM Carbon Design System components for educational value demonstration
 * Navigation Flow: Landing → Value Content → ROI Calculator → Success Stories → Contact Sales
 */

import React, { useState } from 'react';
import {
  Grid,
  Column,
  Tile,
  ClickableTile,
  Button,
  ButtonSet,
  Tag,
  Modal,
  DataTable,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  ProgressIndicator,
  ProgressStep,
  ContentSwitcher,
  Switch,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Theme,
  Section,
  Stack,
} from '@carbon/react';
import {
  ArrowRight,
  Education,
  Analytics,
  Time,
  Security,
  ChartLineData,
  Trophy,
  CheckmarkFilled,
  Calculator,
  Document,
  UserMultiple,
  Star,
  Calendar,
  Download,
  PlayOutline,
  Location,
  Email,
} from '@carbon/icons-react';
import { MarketingVariant } from '../../routes/MarketingRouter';
import MarketingHeader from '../../components/marketing/MarketingHeader';
import MarketingFooter from '../../components/marketing/MarketingFooter';
import { useNavigate } from 'react-router-dom';
import styles from './SuccessStoriesPage.module.scss';

interface SuccessStoriesPageProps {
  variant?: MarketingVariant;
}

interface DetailedCaseStudy {
  id: string;
  institution: string;
  type: string;
  location: string;
  studentCount: number;
  implementationDate: string;
  challenge: string;
  solution: string;
  implementation: {
    timeline: string;
    phases: string[];
    training: string;
  };
  results: {
    category: string;
    metrics: { label: string; before: string; after: string; improvement: string }[];
  }[];
  testimonials: {
    quote: string;
    author: string;
    role: string;
    image?: string;
  }[];
  resources: {
    caseStudyPdf: string;
    video: string;
    webinar: string;
  };
  featured: boolean;
}

const SuccessStoriesPage: React.FC<SuccessStoriesPageProps> = ({ variant = 'default' }) => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStory, setSelectedStory] = useState<DetailedCaseStudy | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const getSuccessStories = (): DetailedCaseStudy[] => {
    return [
      {
        id: 'metro-district',
        institution: 'Metro School District',
        type: 'Large Urban District',
        location: 'Chicago, IL',
        studentCount: 45000,
        implementationDate: '2023-08',
        challenge:
          'Declining test scores, low teacher retention, and inconsistent educational quality across 150 schools',
        solution:
          'District-wide implementation of Sigma08 AI-powered teaching tools, analytics platform, and family engagement system',
        implementation: {
          timeline: '6 months',
          phases: [
            'Pilot program with 5 schools (Month 1-2)',
            'Teacher training and onboarding (Month 2-3)',
            'Gradual rollout to all schools (Month 3-5)',
            'Full implementation and optimization (Month 6)',
          ],
          training: '40 hours of professional development per teacher',
        },
        results: [
          {
            category: 'Academic Performance',
            metrics: [
              { label: 'Reading Proficiency', before: '58%', after: '81%', improvement: '+40%' },
              { label: 'Math Proficiency', before: '52%', after: '78%', improvement: '+50%' },
              { label: 'Science Scores', before: '61%', after: '84%', improvement: '+38%' },
              { label: 'Graduation Rate', before: '73%', after: '91%', improvement: '+25%' },
            ],
          },
          {
            category: 'Operational Efficiency',
            metrics: [
              { label: 'Teacher Retention', before: '68%', after: '89%', improvement: '+31%' },
              { label: 'Admin Time Saved', before: '0hrs', after: '20hrs/week', improvement: '+100%' },
              { label: 'Parent Engagement', before: '42%', after: '78%', improvement: '+86%' },
              { label: 'Cost Per Student', before: '$12,500', after: '$10,200', improvement: '-18%' },
            ],
          },
        ],
        testimonials: [
          {
            quote:
              'Sigma08 has transformed our entire district. The data-driven insights have revolutionized how we approach education, and our teachers feel empowered with the AI tools.',
            author: 'Dr. Patricia Williams',
            role: 'Superintendent',
          },
          {
            quote:
              "For the first time in years, I'm excited about teaching again. The AI helps me create personalized lessons for each student.",
            author: 'Maria Rodriguez',
            role: '3rd Grade Teacher',
          },
        ],
        resources: {
          caseStudyPdf: '/resources/metro-district-case-study.pdf',
          video: 'https://youtu.be/example1',
          webinar: '/webinars/metro-district-transformation',
        },
        featured: true,
      },
      {
        id: 'lincoln-elementary',
        institution: 'Lincoln Elementary School',
        type: 'K-5 Elementary',
        location: 'Austin, TX',
        studentCount: 450,
        implementationDate: '2023-09',
        challenge: 'Low reading comprehension scores (45% below grade level) and minimal parent engagement',
        solution: 'Personalized reading program with AI tutoring and comprehensive family engagement platform',
        implementation: {
          timeline: '3 months',
          phases: [
            'Reading assessment and baseline establishment',
            'AI tutoring system deployment',
            'Parent portal and engagement tools launch',
            'Continuous optimization and support',
          ],
          training: '20 hours of teacher training, 10 hours parent orientation',
        },
        results: [
          {
            category: 'Reading Achievement',
            metrics: [
              { label: 'Grade-Level Reading', before: '55%', after: '89%', improvement: '+62%' },
              { label: 'Reading Comprehension', before: '48%', after: '85%', improvement: '+77%' },
              { label: 'Reading Engagement', before: '62%', after: '94%', improvement: '+52%' },
              { label: 'Library Book Checkouts', before: '1.2/month', after: '3.8/month', improvement: '+217%' },
            ],
          },
          {
            category: 'Family Engagement',
            metrics: [
              { label: 'Parent Portal Usage', before: '15%', after: '87%', improvement: '+480%' },
              { label: 'Parent-Teacher Conferences', before: '68%', after: '96%', improvement: '+41%' },
              { label: 'Home Reading Time', before: '12min/day', after: '35min/day', improvement: '+192%' },
              { label: 'Homework Completion', before: '74%', after: '97%', improvement: '+31%' },
            ],
          },
        ],
        testimonials: [
          {
            quote:
              "The transformation has been incredible. Our students are reading with confidence, and parents are actively involved in their children's learning journey.",
            author: 'Sarah Martinez',
            role: 'Principal',
          },
          {
            quote:
              'My daughter went from struggling with reading to loving books. The AI tutor makes learning fun and personalized.',
            author: 'Jennifer Thompson',
            role: 'Parent',
          },
        ],
        resources: {
          caseStudyPdf: '/resources/lincoln-elementary-case-study.pdf',
          video: 'https://youtu.be/example2',
          webinar: '/webinars/elementary-reading-success',
        },
        featured: true,
      },
      {
        id: 'westfield-high',
        institution: 'Westfield High School',
        type: '9-12 High School',
        location: 'Seattle, WA',
        studentCount: 1200,
        implementationDate: '2023-06',
        challenge: 'High failure rates in STEM subjects (45% in math, 38% in science) and low college readiness',
        solution: 'AI-powered STEM tutoring, adaptive learning paths, and college preparation program',
        implementation: {
          timeline: '4 months',
          phases: [
            'STEM assessment and gap analysis',
            'AI tutoring system for math and science',
            'College prep and career guidance integration',
            'Advanced analytics and intervention system',
          ],
          training: '30 hours STEM teacher training, 15 hours counselor training',
        },
        results: [
          {
            category: 'STEM Performance',
            metrics: [
              { label: 'Math Proficiency', before: '55%', after: '84%', improvement: '+53%' },
              { label: 'Science Proficiency', before: '62%', after: '87%', improvement: '+40%' },
              { label: 'AP STEM Pass Rate', before: '68%', after: '91%', improvement: '+34%' },
              { label: 'STEM Course Enrollment', before: '420 students', after: '780 students', improvement: '+86%' },
            ],
          },
          {
            category: 'College Readiness',
            metrics: [
              { label: 'SAT Math Scores', before: '485 avg', after: '562 avg', improvement: '+16%' },
              { label: 'College Enrollment', before: '67%', after: '89%', improvement: '+33%' },
              { label: 'STEM Major Intent', before: '23%', after: '41%', improvement: '+78%' },
              { label: 'Scholarship Awards', before: '$450K', after: '$1.2M', improvement: '+167%' },
            ],
          },
        ],
        testimonials: [
          {
            quote:
              'Our students are now confident in STEM subjects. The AI tutoring provides exactly the support they need when they need it.',
            author: 'Michael Chen',
            role: 'Math Department Head',
          },
          {
            quote:
              "I never thought I'd be good at physics, but the AI tutor helped me understand concepts I'd been struggling with for months.",
            author: 'Alexandra Kim',
            role: 'Junior Student',
          },
        ],
        resources: {
          caseStudyPdf: '/resources/westfield-high-case-study.pdf',
          video: 'https://youtu.be/example3',
          webinar: '/webinars/stem-success-strategies',
        },
        featured: false,
      },
    ];
  };

  const successStories = getSuccessStories();
  const filteredStories =
    selectedCategory === 'all'
      ? successStories
      : successStories.filter(story =>
          selectedCategory === 'elementary'
            ? story.type.includes('Elementary')
            : selectedCategory === 'high-school'
              ? story.type.includes('High School')
              : selectedCategory === 'district'
                ? story.type.includes('District')
                : story.type === selectedCategory
        );

  const handleViewFullStory = (story: DetailedCaseStudy) => {
    setSelectedStory(story);
    setIsModalOpen(true);
  };

  const handleScheduleDemo = () => {
    navigate('/demo');
  };

  const handleDownloadCaseStudy = (pdfUrl: string) => {
    window.open(pdfUrl, '_blank');
  };

  return (
    <div className={styles.successStoriesPage}>
      <MarketingHeader variant={variant} />

      {/* Progress Indicator */}
      <Section className={`cds--layout--size-lg ${styles.progressSection}`}>
        <Grid>
          <Column lg={16} md={8} sm={4}>
            <ProgressIndicator currentIndex={3} spaceEqually>
              <ProgressStep label="Discover Platform" complete />
              <ProgressStep label="Explore Value" complete />
              <ProgressStep label="Calculate ROI" complete />
              <ProgressStep label="Success Stories" />
              <ProgressStep label="Get Started" />
            </ProgressIndicator>
          </Column>
        </Grid>
      </Section>

      {/* Hero Section */}
      <Section className="cds--layout--size-xl">
        <Grid>
          <Column lg={12} md={6} sm={4}>
            <Stack gap={6}>
              <h1 className={styles.heroTitle}>Transformational Success Stories</h1>
              <p className={styles.heroDescription}>
                Discover how educational institutions across the country have achieved remarkable improvements in
                student outcomes, teacher satisfaction, and operational efficiency with Sigma08's AI-powered learning
                platform.
              </p>
              <Stack gap={3} orientation="horizontal" className="cds--layout--wrap">
                <Tag type="purple" size="lg">
                  <Trophy /> 200+ Schools Transformed
                </Tag>
                <Tag type="green" size="lg">
                  <CheckmarkFilled /> 250K+ Students Impacted
                </Tag>
                <Tag type="blue" size="lg">
                  <Star /> 98% Satisfaction Rate
                </Tag>
              </Stack>
            </Stack>
          </Column>
          <Column lg={4} md={2} sm={4}>
            <Tile className={`cds--layout--size-lg ${styles.metricsTile}`}>
              <Stack gap={4}>
                <div>
                  <div className={styles.metricValue}>+35%</div>
                  <div className={styles.metricLabel}>Average Performance Improvement</div>
                </div>
                <div>
                  <div className={`${styles.metricValue} ${styles.success}`}>$2.3M</div>
                  <div className={styles.metricLabel}>Average Annual Savings</div>
                </div>
              </Stack>
            </Tile>
          </Column>
        </Grid>
      </Section>

      {/* Category Filter */}
      <Section className={`cds--layout--size-lg ${styles.progressSection}`}>
        <Grid>
          <Column lg={16} md={8} sm={4}>
            <ContentSwitcher
              selectedIndex={['all', 'elementary', 'high-school', 'district'].indexOf(selectedCategory)}
              onChange={index => {
                const categories = ['all', 'elementary', 'high-school', 'district'];
                setSelectedCategory(categories[index]);
              }}
            >
              <Switch name="all" text="All Stories" />
              <Switch name="elementary" text="Elementary Schools" />
              <Switch name="high-school" text="High Schools" />
              <Switch name="district" text="School Districts" />
            </ContentSwitcher>
          </Column>
        </Grid>
      </Section>

      {/* Featured Stories */}
      <Section className="cds--layout--size-xl">
        <Grid>
          <Column lg={16} md={8} sm={4}>
            <Stack gap={9}>
              <h2 className={styles.sectionTitle}>Featured Success Stories</h2>

              <div className={styles.storiesGrid}>
                {filteredStories
                  .filter(story => story.featured)
                  .map(story => (
                    <Tile key={story.id} className={styles.storyCard}>
                      <Stack gap={5}>
                        <div className={styles.storyHeader}>
                          <Stack gap={3}>
                            <h3 className={styles.storyTitle}>{story.institution}</h3>
                            <Stack gap={2} orientation="horizontal">
                              <Tag type="blue" size="sm">
                                {story.type}
                              </Tag>
                              <Tag type="gray" size="sm">
                                <Location size={12} /> {story.location}
                              </Tag>
                            </Stack>
                          </Stack>
                          <Tag type="purple" size="sm">
                            Featured
                          </Tag>
                        </div>

                        <p className={styles.storyChallenge}>{story.challenge}</p>

                        <div className={styles.metricsGrid}>
                          {story.results[0].metrics.slice(0, 2).map((metric, i) => (
                            <Tile key={i} className={styles.metricTile}>
                              <Stack gap={2}>
                                <div className={styles.metricAfter}>{metric.after}</div>
                                <div className={styles.metricLabel}>{metric.label}</div>
                                <Tag type="green" size="sm">
                                  {metric.improvement}
                                </Tag>
                              </Stack>
                            </Tile>
                          ))}
                        </div>

                        <blockquote className={styles.testimonialQuote}>
                          <Stack gap={2}>
                            <p className="cds--type-body-long-01">
                              "{story.testimonials[0].quote.substring(0, 120)}..."
                            </p>
                            <div className={styles.quoteAuthor}>
                              {story.testimonials[0].author}, {story.testimonials[0].role}
                            </div>
                          </Stack>
                        </blockquote>

                        <ButtonSet>
                          <Button
                            kind="primary"
                            size="md"
                            renderIcon={ArrowRight}
                            onClick={() => handleViewFullStory(story)}
                          >
                            Read Full Story
                          </Button>
                          <Button
                            kind="ghost"
                            size="md"
                            renderIcon={Download}
                            onClick={() => handleDownloadCaseStudy(story.resources.caseStudyPdf)}
                          >
                            Download PDF
                          </Button>
                        </ButtonSet>
                      </Stack>
                    </Tile>
                  ))}
              </div>
            </Stack>
          </Column>
        </Grid>
      </Section>

      {/* All Stories Grid */}
      <Section className={styles.allStoriesSection}>
        <Grid>
          <Column lg={16} md={8} sm={4}>
            <Stack gap={7}>
              <h2 className={styles.sectionTitle}>More Success Stories</h2>

              <div className={styles.storiesGrid}>
                {filteredStories
                  .filter(story => !story.featured)
                  .map(story => (
                    <ClickableTile
                      key={story.id}
                      onClick={() => handleViewFullStory(story)}
                      className={styles.storyCardSmall}
                    >
                      <Stack gap={5}>
                        <Stack gap={3}>
                          <h3 className={styles.storyTitle}>{story.institution}</h3>
                          <Stack gap={2} orientation="horizontal">
                            <Tag type="blue" size="sm">
                              {story.type}
                            </Tag>
                            <Tag type="gray" size="sm">
                              {story.studentCount.toLocaleString()} students
                            </Tag>
                          </Stack>
                        </Stack>

                        <p className={styles.storyLocation}>{story.challenge.substring(0, 100)}...</p>

                        <div className={styles.storyBottom}>
                          <div className={styles.improvementValue}>{story.results[0].metrics[0].improvement}</div>
                          <Button kind="ghost" size="sm" renderIcon={ArrowRight}>
                            Read More
                          </Button>
                        </div>
                      </Stack>
                    </ClickableTile>
                  ))}
              </div>
            </Stack>
          </Column>
        </Grid>
      </Section>

      {/* CTA Section */}
      <Section className={styles.ctaSection}>
        <Grid>
          <Column lg={12} md={6} sm={4}>
            <Stack gap={6}>
              <h2 className={styles.ctaTitle}>Ready to Write Your Success Story?</h2>
              <p className={styles.ctaDescription}>
                Join hundreds of educational institutions transforming their outcomes with Sigma08. Schedule a
                personalized demo to see how we can help you achieve similar results.
              </p>
            </Stack>
          </Column>
          <Column lg={4} md={2} sm={4}>
            <ButtonSet stacked>
              <Button kind="secondary" size="lg" renderIcon={Calendar} onClick={handleScheduleDemo}>
                Schedule Your Demo
              </Button>
              <Button kind="tertiary" size="lg" renderIcon={Calculator} onClick={() => navigate('/calculators')}>
                Calculate Your ROI
              </Button>
            </ButtonSet>
          </Column>
        </Grid>
      </Section>

      {/* Detailed Story Modal */}
      <Modal
        open={isModalOpen}
        onRequestClose={() => setIsModalOpen(false)}
        modalHeading={selectedStory?.institution}
        modalLabel="Detailed Case Study"
        primaryButtonText="Schedule Demo"
        secondaryButtonText="Download PDF"
        onRequestSubmit={handleScheduleDemo}
        onSecondarySubmit={() => selectedStory && handleDownloadCaseStudy(selectedStory.resources.caseStudyPdf)}
        size="lg"
      >
        {selectedStory && (
          <div className={styles.modalContent}>
            <Tabs>
              <TabList>
                <Tab>Overview</Tab>
                <Tab>Results</Tab>
                <Tab>Implementation</Tab>
                <Tab>Testimonials</Tab>
              </TabList>
              <TabPanels>
                <TabPanel>
                  <Stack gap={5} className="cds--layout--size-sm">
                    <Stack gap={3} orientation="horizontal">
                      <Tag type="blue">{selectedStory.type}</Tag>
                      <Tag type="gray">{selectedStory.location}</Tag>
                      <Tag type="purple">{selectedStory.studentCount.toLocaleString()} students</Tag>
                    </Stack>

                    <Stack gap={3}>
                      <h4 className="cds--type-heading-04">Challenge</h4>
                      <p className={styles.storyChallenge}>{selectedStory.challenge}</p>
                    </Stack>

                    <Stack gap={3}>
                      <h4 className="cds--type-heading-04">Solution</h4>
                      <p className={styles.storyChallenge}>{selectedStory.solution}</p>
                    </Stack>

                    <Stack gap={3}>
                      <h4 className="cds--type-heading-04">Implementation Timeline</h4>
                      <Tag type="green" size="md">
                        {selectedStory.implementation.timeline}
                      </Tag>
                    </Stack>
                  </Stack>
                </TabPanel>

                <TabPanel>
                  <Stack gap={7} className="cds--layout--size-sm">
                    {selectedStory.results.map((category, index) => (
                      <Stack key={index} gap={5}>
                        <h4 className="cds--type-heading-04">{category.category}</h4>
                        <div className={styles.resultsMetricsGrid}>
                          {category.metrics.map((metric, i) => (
                            <Tile key={i} className={styles.resultMetric}>
                              <Stack gap={3}>
                                <div className={styles.metricLabel}>{metric.label}</div>
                                <div className={styles.metricComparison}>
                                  <span className={styles.beforeValue}>Before: {metric.before}</span>
                                  <span className={styles.afterValue}>After: {metric.after}</span>
                                </div>
                                <Tag type="green" size="sm">
                                  {metric.improvement}
                                </Tag>
                              </Stack>
                            </Tile>
                          ))}
                        </div>
                      </Stack>
                    ))}
                  </Stack>
                </TabPanel>

                <TabPanel>
                  <Stack gap={6} className="cds--layout--size-sm">
                    <Stack gap={4}>
                      <h4 className="cds--type-heading-04">Implementation Phases</h4>
                      <ol className="cds--list--ordered">
                        {selectedStory.implementation.phases.map((phase, i) => (
                          <li key={i} className="cds--list__item">
                            {phase}
                          </li>
                        ))}
                      </ol>
                    </Stack>

                    <Stack gap={3}>
                      <h4 className="cds--type-heading-04">Training Provided</h4>
                      <p className={styles.storyChallenge}>{selectedStory.implementation.training}</p>
                    </Stack>
                  </Stack>
                </TabPanel>

                <TabPanel>
                  <Stack gap={5} className="cds--layout--size-sm">
                    {selectedStory.testimonials.map((testimonial, i) => (
                      <blockquote key={i} className={styles.testimonialBlock}>
                        <Stack gap={3}>
                          <p className="cds--type-body-long-01">"{testimonial.quote}"</p>
                          <div className={styles.quoteAuthor}>
                            {testimonial.author}, {testimonial.role}
                          </div>
                        </Stack>
                      </blockquote>
                    ))}
                  </Stack>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </div>
        )}
      </Modal>

      <MarketingFooter />
    </div>
  );
};

export default SuccessStoriesPage;
